import Box from "@cloudscape-design/components/box"
import ButtonDropdown from "@cloudscape-design/components/button-dropdown"
import Checkbox from "@cloudscape-design/components/checkbox"
import Header from "@cloudscape-design/components/header"
import SpaceBetween from "@cloudscape-design/components/space-between"
import { useState } from "react"
import type { TestConceptGroup } from "@/generated/sdk"
import type { DTADocumentContent } from "../../../types/dta"
import type { InlineDiffResult } from "../../../utils/inlineDiff"
import { DTADetails } from "../../../services/dtaApi"
import { DiffBadge } from "../DiffBadge"
import { TestConceptEditor } from "./TestConceptEditor"
import { TestConceptsImportWizard } from "./TestConceptsImportWizard"
import { isAutoRepaired, testConceptHasNoValueColumn } from "./validation"

interface TabContentProps {
  dta: DTADetails
  content: DTADocumentContent
  onUpdate: (updates: Partial<DTADocumentContent>) => void
  canEdit: boolean
  showErrors?: boolean
  inlineDiff?: InlineDiffResult | null
}

const MAX_ITEMS = 20

export const TestConceptsTab: React.FC<TabContentProps> = ({
  dta,
  content,
  onUpdate,
  canEdit,
  showErrors = false,
  inlineDiff,
}) => {
  const items = content.test_concepts || []
  const [importOpen, setImportOpen] = useState(false)
  const columnNames = (content.transfer_metadata_schema?.variables || [])
    .map((v) => v.variable_name)
    .filter((n): n is string => !!n)

  const handleChange = (index: number, updated: TestConceptGroup) => {
    const next = [...items]
    next[index] = updated
    onUpdate({ test_concepts: next })
  }

  const handleRemove = (index: number) => {
    onUpdate({ test_concepts: items.filter((_, i) => i !== index) })
  }

  // Auto-repaired groups are reviewed and enabled one at a time, never in bulk:
  // enabling several at once can add rules that reject each other's rows.
  const toggleable = items.filter((it) => !isAutoRepaired(it))
  const enabledCount = toggleable.filter((it) => it.enabled !== false).length
  const allEnabled = toggleable.length > 0 && enabledCount === toggleable.length
  const someEnabled = enabledCount > 0 && enabledCount < toggleable.length

  const handleToggleAll = () => {
    const newValue = enabledCount < toggleable.length
    onUpdate({
      test_concepts: items.map((it) => (isAutoRepaired(it) ? it : { ...it, enabled: newValue })),
    })
  }

  const handleAdd = () => {
    onUpdate({
      test_concepts: [
        ...items,
        { id: crypto.randomUUID(), conditionColumns: [], valueColumns: [], rows: [] },
      ],
    })
  }

  const handleImport = (imported: TestConceptGroup[]) => {
    // Auto-add columns to metadata schema if missing
    const schema = content.transfer_metadata_schema || { variables: [] }
    const variables = schema.variables ?? []
    const existingVars = variables.map((v) => v.variable_name).filter(Boolean) as string[]
    const allImportedCols = [
      ...new Set(imported.flatMap((g) => [...g.conditionColumns, ...g.valueColumns])),
    ].filter((col) => col && !existingVars.includes(col))

    let nextOrder = variables.reduce((max, v) => Math.max(max, Number(v.order) || 0), 0) + 1
    const updatedSchema =
      allImportedCols.length > 0
        ? {
            variables: [
              ...variables,
              ...allImportedCols.map((col) => ({ variable_name: col, order: nextOrder++ })),
            ],
          }
        : undefined

    // Merge: if an existing group has same conditionColumns + valueColumns, merge rows
    const colKey = (g: TestConceptGroup) =>
      [...g.conditionColumns].sort().join("\x00") + "\x01" + [...g.valueColumns].sort().join("\x00")

    const merged = [...items]
    for (const imp of imported) {
      const key = colKey(imp)
      const existing = merged.find((m) => colKey(m) === key)
      if (existing) {
        const existingKeys = new Set(existing.rows.map((r) => JSON.stringify(r)))
        for (const row of imp.rows) {
          if (!existingKeys.has(JSON.stringify(row))) existing.rows.push(row)
        }
      } else {
        merged.push(imp)
      }
    }

    onUpdate({
      test_concepts: merged.slice(0, MAX_ITEMS),
      ...(updatedSchema ? { transfer_metadata_schema: updatedSchema } : {}),
    })
  }

  return (
    <SpaceBetween size="l">
      <TestConceptsImportWizard
        isOpen={importOpen}
        onClose={() => setImportOpen(false)}
        onImport={handleImport}
      />
      <Header
        variant="h3"
        actions={
          canEdit && (
            <ButtonDropdown
              items={[
                { id: "blank", text: "Create blank" },
                { id: "csv", text: "Import from CSV" },
              ]}
              onItemClick={({ detail }) => {
                if (detail.id === "blank") handleAdd()
                else if (detail.id === "csv") setImportOpen(true)
              }}
            >
              + Add Test Concept
            </ButtonDropdown>
          )
        }
      />

      {canEdit && toggleable.length > 0 && (
        <Checkbox checked={allEnabled} indeterminate={someEnabled} onChange={handleToggleAll}>
          Enable all
        </Checkbox>
      )}

      {items.length === 0 ? (
        <Box textAlign="center" color="text-status-inactive" padding={{ vertical: "xl" }}>
          <SpaceBetween size="xs">
            <span>No test concepts defined yet.</span>
            <Box fontSize="body-s" color="text-status-inactive">
              Import from a CSV/TSV or add a group manually.
            </Box>
          </SpaceBetween>
        </Box>
      ) : (
        <SpaceBetween size="m">
          {items.map((item, i) => (
            <div
              key={item.id}
              data-terminology-item
              data-invalid={showErrors && testConceptHasNoValueColumn(item) ? "true" : undefined}
            >
              {item.id && inlineDiff?.testConcepts.get(item.id) && (
                <DiffBadge
                  status={inlineDiff.testConcepts.get(item.id)!}
                  version={dta.version}
                  dtaId={dta.dta_id}
                  section="test_concepts"
                  item={item.id}
                />
              )}
              <TestConceptEditor
                key={item.id}
                item={item}
                index={i}
                onChange={handleChange}
                onRemove={handleRemove}
                canEdit={canEdit}
                columnNames={columnNames}
                showErrors={showErrors}
              />
            </div>
          ))}
        </SpaceBetween>
      )}
    </SpaceBetween>
  )
}
